"use client";

import * as Api from "@api";
import * as C from "@components";
import React from "react";
import { ProductSpecificationKeys } from "./PageContentKeys";
import styles from "./product-specification.module.scss";

const enum Tab
{
    Benefits,
    Specs,
}

export interface ProductSpecificationProps<PageContentKey extends string>
{
    pageContent: Api.Content<typeof ProductSpecificationKeys[number]>;
}

export const ProductSpecification = <PageContentKey extends string>({ pageContent }: ProductSpecificationProps<PageContentKey>): React.FunctionComponentElement<ProductSpecificationProps<PageContentKey>> => 
{
    const [activeTab, setActiveTab] = React.useState<Tab>(Tab.Benefits);

    const mainRef = React.useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() =>
    {
        const mainElement = mainRef.current;

        if (mainElement !== null)
        {
            const switches = mainElement.querySelectorAll(":scope > nav > [data-tab]");

            for (let i = 0; i < switches.length; i++)
            {
                const sw = switches[i] as HTMLButtonElement;
                sw.classList.toggle("active", Number(sw.dataset.tab) === activeTab);
            }

            const contents = mainElement.querySelectorAll(`:scope > [data-tab]`);

            for (let i = 0; i < contents.length; i++)
            {
                const content = contents[i] as HTMLElement;
                const isActive = Number(content.dataset.tab) === activeTab;
                content.classList.toggle("transparent", isActive);
                content.classList.toggle("hidden", !isActive);
            }

            requestAnimationFrame(() =>
            {
                for (let i = 0; i < contents.length; i++)
                {
                    const content = contents[i] as HTMLElement;
                    content.classList.remove("transparent");
                }
            });
        }
    }, [activeTab]);

    const handleTabClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(event =>
    {
        event.preventDefault();
        setActiveTab(Number(event.currentTarget.dataset.tab));
    }, []);

    return (
        <div className={styles["specification"]} ref={mainRef}>
            <nav>
                <button data-tab={Tab.Benefits} onClick={handleTabClick} type="button">Benefits</button>
                <button data-tab={Tab.Specs} onClick={handleTabClick} type="button">Specs</button>
            </nav>
            {activeTab === Tab.Benefits && <C.PageContent {...pageContent["specification.benefits"]} data-tab={Tab.Benefits} typography={C.Typography.P3} />}
            {activeTab === Tab.Specs && <C.PageContent {...pageContent["specification.specs"]} data-tab={Tab.Specs} typography={C.Typography.P3} />}
        </div>
    );
}